import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  useTheme,
  Typography,
  Fade,
} from '@mui/material';
import { toast } from 'react-toastify';
import { saveWorkflow } from '../../config/workflowApi';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../Context/ThemeContext';

import { useDispatch, useSelector } from 'react-redux';
import { addWorkflow } from '../../state/slices/workflowSlice';

const NewWorkflowModal = ({ selectedProject }) => {
  const [open, setOpen] = useState(false);
  const [localWorkflowName, setLocalWorkflowName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const existingWorkflows = useSelector((state) => state.workflows.list);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setLocalWorkflowName('');
    setDescription('');
    setError(false);
    setErrorMessage('');
  };

  const handleSave = async () => {
    if (!localWorkflowName.trim()) {
      setError(true);
      toast.error('Please enter the Workflow Name!', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    // ✅ Check for duplicate name within selected project
    // const isDuplicate = existingWorkflows.some(
    //   (wf) =>
    //     wf.project_id === selectedProject?.project_id &&
    //     wf.name === localWorkflowName.trim() //case sensitive
    //   // wf.name.toLowerCase() === localWorkflowName.trim().toLowerCase() //case insensitive
    // );

    const isDuplicate = existingWorkflows.some(
      (wf) => wf.name === localWorkflowName.trim(),
    );

    if (isDuplicate) {
      setError(true);
      toast.error('A workflow with this name already exists in this project.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const userId = Cookies.get('userId');
    console.log('userId:', userId);
    if (!userId) {
      toast.error('User session expired. Please login again.');
      return;
    }

    const workflowData = {
      project_id: selectedProject?.project_id,
      user_id: userId,
      name: localWorkflowName,
      environment: 'test',
      is_active: true,
      description: description || 'Test',
    };

    try {
      setLoading(true);
      const savedWorkflow = await saveWorkflow(workflowData);

      // ✅ Update Redux store immediately
      dispatch(addWorkflow(savedWorkflow));

      console.log('savedWorkflow:::', savedWorkflow);

      toast.success('Workflow saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
      });
      handleClose();

      // ✅ Navigate to workflow page after save
      // setTimeout(() => {
      //   navigate(`/parentworkflow/${localWorkflowName}`);
      // }, 3000);
      setTimeout(() => {
        navigate(`/parentworkflow/${savedWorkflow?.workflow_id}`);
      }, 3000);
    } catch (error) {
      toast.error('Error saving workflow. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{
          fontSize: '0.875rem',
          backgroundColor: '#5965A6',
          padding: '6px 14px',
          color: 'white',
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 500,
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          transition:
            'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease',
          willChange: 'transform, box-shadow',
          '&:hover': {
            backgroundColor: '#4C5B9A', // keep same color
            transform: 'translateY(-1px) scale(1.02)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
          },
          '&:active': {
            transform: 'translateY(0) scale(0.99)',
            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
          },
        }}
      >
        + Add New
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(39, 34, 34, 0.64)',
            backdropFilter: 'blur(2px)',
          },
        }}
        PaperProps={{
          sx: {
            width: '500px',
            borderRadius: '16px',
            background: darkMode
              ? 'linear-gradient(135deg, #1a1a1c 0%, #2c2c2e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(79, 172, 254, 0.1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background:
                'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #4facfe 100%)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(79, 172, 254, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#1a202c',
                letterSpacing: '-0.02em',
              }}
            >
              Create New Workflow
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            overflowX: 'hidden',
            overflowY: 'auto',
            maxHeight: '65vh',
            p: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 16,
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#374151',
                mb: 1,
              }}
            >
              Workflow Name
            </Typography>

            <TextField
              label=""
              placeholder="Enter workflow name here"
              fullWidth
              variant="outlined"
              value={localWorkflowName}
              onChange={(e) => {
                const name = e.target.value;
                setLocalWorkflowName(name);

                if (!name.trim()) {
                  setError(true);
                  setErrorMessage('Workflow Name is required!');
                  return;
                }

                // const isDuplicate = existingWorkflows.some(
                //   (wf) =>
                //     wf.project_id === selectedProject?.project_id &&
                //     wf.name === name.trim()
                // );

                const isDuplicate = existingWorkflows.some(
                  (wf) => wf.name === name.trim(),
                );

                if (isDuplicate) {
                  setError(true);
                  setErrorMessage(
                    'A workflow with this exact name already exists in this project.',
                  );
                } else {
                  setError(false);
                  setErrorMessage('');
                }
              }}
              error={error}
              helperText={error ? errorMessage : ''}
              autoFocus
              InputLabelProps={{
                style: { color: theme.palette.text.secondary },
              }}
              InputProps={{
                sx: {
                  '& .MuiInputBase-input': {
                    paddingTop: '12px',
                    paddingBottom: '12px',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: darkMode ? '#2c2c2e' : '#ffffff',
                  border: '1px solid rgba(79, 172, 254, 0.1)',
                  boxShadow: darkMode
                    ? 'inset 0 1px 2px rgba(255, 255, 255, 0.04)'
                    : 'inset 0 1px 2px rgba(0, 0, 0, 0.06)',
                  '&:hover': {
                    borderColor: 'rgba(79, 172, 254, 0.3)',
                  },
                  '&.Mui-focused': {
                    borderColor: '#4facfe',
                    boxShadow: '0 0 0 3px rgba(79, 172, 254, 0.1)',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: 16,
                fontWeight: 600,

                color: darkMode ? '#ffffff' : '#374151',
                mb: 1,
              }}
            >
              Description
            </Typography>

            <TextField
              label=""
              placeholder="Enter workflow description (optional)"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              InputLabelProps={{
                style: { color: theme.palette.text.secondary },
              }}
              InputProps={{
                sx: {},
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: darkMode ? '#2c2c2e' : '#ffffff',
                  border: '1px solid rgba(79, 172, 254, 0.1)',
                  boxShadow: darkMode
                    ? 'inset 0 1px 2px rgba(255, 255, 255, 0.04)'
                    : 'inset 0 1px 2px rgba(0, 0, 0, 0.06)',
                  '&:hover': {
                    borderColor: 'rgba(79, 172, 254, 0.3)',
                  },
                  '&.Mui-focused': {
                    borderColor: '#4facfe',
                    boxShadow: '0 0 0 3px rgba(79, 172, 254, 0.1)',
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 4,
              gap: 2,
              pt: 2.5,
              borderTop: '1px solid rgba(79, 172, 254, 0.1)',
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',

                fontSize: '0.875rem',
                fontWeight: 500,
                px: 3,
                borderColor: 'rgba(79, 172, 254, 0.59)',
                color: '#4facfe',
                '&:hover': {
                  borderColor: '#4facfe',
                  background: 'rgba(79, 172, 254, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!localWorkflowName.trim() || loading}
              variant="contained"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',

                fontSize: '0.875rem',
                fontWeight: 600,
                px: 3,
                background: !localWorkflowName.trim()
                  ? darkMode
                    ? '#444'
                    : '#ccc'
                  : 'linear-gradient(90deg, #4facfe, #00d4ff)',
                color: !localWorkflowName.trim()
                  ? darkMode
                    ? '#888'
                    : '#666'
                  : '#ffffff',
                '&:hover': {
                  background: !localWorkflowName.trim()
                    ? darkMode
                      ? '#444'
                      : '#ccc'
                    : 'linear-gradient(90deg, #3b9cfe, #00c4ef)',
                  transform: localWorkflowName.trim() ? 'scale(1.02)' : 'none',
                },
                '&:disabled': {
                  background: darkMode ? '#444' : '#ccc',
                  color: darkMode ? '#888' : '#666',
                },
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

NewWorkflowModal.propTypes = {
  selectedProject: PropTypes.shape({
    project_id: PropTypes.string.isRequired,
  }),
};

export default NewWorkflowModal;
